import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { notificationKeys } from '@/services/queryKeys';
import type { RootState } from '@/redux';

/**
 * Realtime notification subscription (EG-355 §4 acceptance criteria).
 *
 * Connect socket.io tới `<VITE_WS_URL>/notification`, join room
 * `tenant:<tenantId>`, lắng `notification.created` / `notification.read` →
 * invalidate query `notifications` để FE re-fetch list + unread count.
 *
 * Graceful degrade: nếu BE chưa có gateway hoặc socket fail → polling 60s
 * (đã có sẵn trong useNotificationList) vẫn chạy → không block UX.
 *
 * Mount 1 lần ở Layout (sau khi authenticated). Không mount nhiều instance —
 * mỗi tab giữ 1 socket connection.
 */
export function useNotificationSocket(): void {
  const qc = useQueryClient();
  const tenantId = useSelector((s: RootState) => s.auth.user?.tenantId);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);

  useEffect(() => {
    if (!tenantId || !accessToken) return;
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (!wsUrl) return;

    const socket: Socket = io(`${wsUrl}/notification`, {
      transports: ['websocket'],
      auth: { token: accessToken, tenantId },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => {
      socket.emit('subscribe_tenant', { tenantId });
    });

    const invalidateNotifications = () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    };

    socket.on('notification.created', invalidateNotifications);
    socket.on('notification.read', invalidateNotifications);
    socket.on('notification.updated', invalidateNotifications);

    socket.on('connect_error', (err) => {
      // Silent fail — polling fallback của useNotifications vẫn chạy.
      // Log debug để dev biết WS chưa wire BE.
      if (import.meta.env.DEV) {
        console.debug('[useNotificationSocket] connect_error:', err.message);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [tenantId, accessToken, qc]);
}
